<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="mb-4">
          <a :href="`/businesses/${businessId}`" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to {{ businessName }}
          </a>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Websites
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ businessName }}
            </p>
          </div>
          <button
            @click="openCreateForm"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Website
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 max-w-sm">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search websites..."
          class="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          @input="debouncedSearch">
      </div>

      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="n in 3" :key="n" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div class="animate-pulse space-y-3">
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          </div>
        </div>
      </div>

      <div v-else-if="websites.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <a
          v-for="website in websites"
          :key="website.id"
          :href="`/businesses/${businessId}/websites/${website.id}`"
          class="block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all">
          <div class="flex items-start justify-between mb-3">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ website.name }}</h3>
            <span :class="statusClass(website.status)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-2 flex-shrink-0">
              {{ website.status }}
            </span>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ website.domain }}</p>
          <div class="mt-4 flex items-center text-xs text-gray-400 dark:text-gray-500">
            <svg class="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ website.pages_count || 0 }} page{{ website.pages_count === 1 ? '' : 's' }}
          </div>
        </a>
      </div>

      <div v-else class="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          No websites
        </h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add your first website for this business.
        </p>
      </div>

      <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Page {{ currentPage }} of {{ totalPages }}
        </p>
        <div class="flex space-x-2">
          <button
            :disabled="currentPage <= 1"
            @click="currentPage--; fetchWebsites()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button
            :disabled="currentPage >= totalPages"
            @click="currentPage++; fetchWebsites()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getBusinessWebsites } from '~/javascript/api/websites.js'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import WebsiteForm from './form.vue'

const props = defineProps({
  businessId: { type: String, required: true },
  businessName: { type: String, default: '' }
})

const websites = ref([])
const loading = ref(true)
const searchQuery = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const slidePanel = ref(null)

const fetchWebsites = async() => {
  try {
    loading.value = true
    const params = { page: currentPage.value }
    if (searchQuery.value) params.q = searchQuery.value

    const response = await getBusinessWebsites(props.businessId, params)
    websites.value = response.data
    totalPages.value = parseInt(response.headers['total-pages'] || '1', 10)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching websites:', error)
  } finally {
    loading.value = false
  }
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    fetchWebsites()
  }, 300)
}

const statusClass = (status) => {
  const map = {
    draft: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  return map[status] || ''
}

const onWebsiteSaved = () => {
  slidePanel.value.close()
  fetchWebsites()
}

const openCreateForm = () => {
  slidePanel.value.open({
    component: WebsiteForm,
    props: { businessId: props.businessId },
    events: { saved: onWebsiteSaved }
  })
}

onMounted(() => {
  hideLoadingSpinner()
  fetchWebsites()
})
</script>
